import { createTheme, CssBaseline, ThemeProvider } from '@mui/material';
import * as locales from '@mui/material/locale';
import { useEffect, useState } from 'react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { useTranslation } from 'react-i18next';
import nbLocale from 'date-fns/locale/nb';
import { useErrorBoundary, withErrorBoundary } from 'react-use-error-boundary';
import AppRoutes from './AppRoutes';
import Axios, { AxiosRequestConfig } from "axios";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import jwtDecode from 'jwt-decode';
import { RedirectToLogin } from './utils';

type SupportedLocales = keyof typeof locales;

export function saveTokenToLocalStorage(token: string) {
    localStorage.setItem("userToken", token);
}
export function getTokenFromLocalStorage() {
    //Also checks for expiration
    const token = localStorage.getItem("userToken");
    if (token) {
        const decoded: any = jwtDecode(token);
        if (decoded && decoded.exp) {
            const tokenTime = parseInt(decoded.exp) * 1000;
            const todayTime = new Date().getTime();
            if (todayTime < tokenTime) {
                return token
            } else {
                removeTokenFromLocalStorage();
                RedirectToLogin();
                return ""
            }
        } else {
            return ""
        }
    }
    else {
        return "";
    }

}
export function removeTokenFromLocalStorage() {
    localStorage.removeItem("userToken");
}

const App = withErrorBoundary((): JSX.Element => {

    const { t, i18n } = useTranslation();

    //The i18n file is set with translation definitions with same language names as MUI.
    const [locale, setLocale] = useState<SupportedLocales>(i18n.language as SupportedLocales);
    //Update the locale whenever user changes the i18n language setting.
    useEffect(() => {
        setLocale(i18n.language as SupportedLocales)
    }, [i18n.language])

    const theme = createTheme({
        palette: {
            primary: {
                main: '#1e87bc',
            },
            secondary: {
                main: '#bc531e',
            },
            background: {
                default: '#efefef',
            },
            info: {
                main: '#34464f',
            },
            success: {
                main: '#1ebca2',
            },
        },
        spacing: (factor: number) => `${0.25 * factor}rem`,
        shape: {
            borderRadius: 2,
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {

                }
            },
        },
    }, locales[locale]);

    //######Edit API connection######
    if (process.env!.NODE_ENV === "production") {
        Axios.defaults.baseURL = "https://uapi.digitalvnxt.no/";
    } else if (process.env!.NODE_ENV === "development") {
        Axios.defaults.baseURL = "https://localhost:3500/";
    } else {
        // set defaults
    }
    //###############################

    const [error, resetError] = useErrorBoundary((error, errorInfo) => {
        console.info("componentDidCatch handler called");
        console.error(error);
        console.error(errorInfo);
    });

    if (error) {
        const message = error instanceof Error ? error.message : (error as string);
        return <>
                <div>Error: {message}</div>
                <button
                    onClick={() => {
                        resetError();
                    }}
                >
                    Reset Error
                </button>
            </>
    }

    return <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={nbLocale} >
            <AppRoutes/>
        </LocalizationProvider>
        <ToastContainer position="bottom-right" />
    </ThemeProvider>
})
export default App;